import { environment } from 'src/environments/environment';

/** Max $ref-following depth when inlining "#/components/schemas/X" pointers below. The
 * curated API's spec (Portal-API/Portal.DevFunctions/OpenApiSpec.cs) currently has ~7
 * component schemas, none of which reference each other — this is a small, fixed safety
 * margin for that, not a generic/unbounded resolver. */
const MAX_REF_DEPTH = 4;

let specPromise: Promise<any> | null = null;

/**
 * Fetches (and caches, for the lifetime of the page) the Developer Platform's OpenAPI
 * 3.0.3 document — served anonymously at GET {devApiUrl}/v1/openapi.json by
 * Portal.DevFunctions/Functions/OpenApiFunction.cs — so the Playground's JSON editor can
 * offer schema-driven autocomplete/validation for whichever endpoint is selected.
 * `environment.devApiUrl` is the same Portal.DevFunctions base URL the Playground itself
 * already sends real requests against (playground.page.ts's `send()`).
 */
export function fetchOpenApiSpec(): Promise<any> {
  if (!specPromise) {
    specPromise = fetch(`${environment.devApiUrl}/v1/openapi.json`)
      .then(res => {
        if (!res.ok) throw new Error(`Failed to load openapi.json (${res.status})`);
        return res.json();
      })
      .catch(err => {
        // Don't poison the cache with a failed fetch — a transient failure shouldn't
        // permanently disable schema support for the rest of the page's lifetime.
        specPromise = null;
        throw err;
      });
  }
  return specPromise;
}

/**
 * Resolves the JSON request-body schema for a given method + Playground path against a
 * previously fetched OpenAPI document (see fetchOpenApiSpec), inlining any
 * "$ref": "#/components/schemas/X" pointers by substituting the referenced schema object.
 * Returns null if the endpoint has no JSON request body (e.g. GET/DELETE) or isn't found
 * in the spec at all.
 *
 * Playground endpoints (playground.page.ts's `endpoints` array) use paths relative to the
 * curated API's "v1" mount point (e.g. "/tablestack/reservations"), while the spec's
 * own `paths` keys include that prefix (e.g. "/v1/tablestack/reservations") — both
 * forms are tried so this stays correct regardless of which convention a caller passes.
 */
export function resolveRequestBodySchema(spec: any, method: string, path: string): object | null {
  if (!spec?.paths) return null;

  const pathItem = spec.paths[path] ?? spec.paths[`/v1${path}`];
  if (!pathItem) return null;

  const operation = pathItem[method.toLowerCase()];
  const schema = operation?.requestBody?.content?.['application/json']?.schema;
  if (!schema) return null;

  return resolveRefs(schema, spec, MAX_REF_DEPTH) as object;
}

function resolveRefs(node: any, spec: any, depthLeft: number): any {
  if (node === null || typeof node !== 'object') return node;

  if (Array.isArray(node)) return node.map(item => resolveRefs(item, spec, depthLeft));

  if (typeof node.$ref === 'string') {
    if (depthLeft <= 0) return {};
    const resolved = resolveJsonPointer(spec, node.$ref);
    return resolved === undefined ? {} : resolveRefs(resolved, spec, depthLeft - 1);
  }

  const result: Record<string, any> = {};
  for (const key of Object.keys(node)) {
    result[key] = resolveRefs(node[key], spec, depthLeft);
  }
  return result;
}

/** Resolves a local "#/a/b/c" JSON pointer against the given document root. */
function resolveJsonPointer(doc: any, ref: string): any {
  if (!ref.startsWith('#/')) return undefined;
  return ref
    .slice(2)
    .split('/')
    .reduce((acc, segment) => (acc === undefined || acc === null ? undefined : acc[segment]), doc);
}
